<script setup>
import { h, computed, onMounted, ref, watch } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage, useWindowSize } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountId, accountScopedRoute, currentAccount, isOnChatwootCloud } =
  useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded items in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
// Using Set to support multiple expanded items (for main groups and subgroups)
const expandedItemsStorage = useStorage(
  'next-sidebar-expanded-items',
  [],
  sessionStorage
);

const expandedItems = computed({
  get: () => new Set(expandedItemsStorage.value || []),
  set: value => {
    expandedItemsStorage.value = Array.from(value);
  },
});

const isItemExpanded = name => {
  return expandedItems.value.has(name);
};

const setExpandedItem = name => {
  const newSet = new Set(expandedItems.value);
  if (newSet.has(name)) {
    newSet.delete(name);
  } else {
    newSet.add(name);
  }
  expandedItems.value = newSet;
};

provideSidebarContext({
  expandedItems,
  isItemExpanded,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

const isEnabledSetting = value => value === true || value === 'true';

const currentUserAccount = computed(() => {
  const accounts = store.getters.getCurrentUser?.accounts || [];
  return (
    accounts.find(account => Number(account.id) === Number(accountId.value)) ||
    {}
  );
});

const canOpenEssential = computed(() => {
  const essentialEnabled =
    isEnabledSetting(
      currentAccount.value?.settings?.essential_interface_enabled
    ) || isEnabledSetting(currentUserAccount.value.essential_interface_enabled);

  return (
    essentialEnabled &&
    (currentUserAccount.value.role === 'administrator' ||
      currentUserAccount.value.interface_mode === 'essential')
  );
});

const isInternalCommunicationEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    'internal_communication'
  )
);

const isMediaLibraryEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    'media_library'
  )
);

watch(
  isInternalCommunicationEnabled,
  enabled => {
    const action = enabled ? 'fetchRooms' : 'reset';
    store.dispatch(`internalCommunication/${action}`);
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

// Sidebar resize functionality
const { width: windowWidth } = useWindowSize();
const sidebarWidth = useStorage('next-sidebar-width', 200);
const isSidebarCollapsed = useStorage('next-sidebar-collapsed', false);
const MIN_WIDTH = 200;
const COLLAPSED_WIDTH = 56;
const MAX_WIDTH = 400;
const isResizing = ref(false);
const resizeStartX = ref(0);
const resizeStartWidth = ref(200);

const isDesktop = computed(() => windowWidth.value >= 768);
const shouldShowCollapsedSidebar = computed(
  () => isDesktop.value && isSidebarCollapsed.value
);

const handleResize = event => {
  if (!isResizing.value) return;

  const deltaX = event.clientX - resizeStartX.value;
  const isRTL = document.documentElement.dir === 'rtl';
  const newWidth = isRTL
    ? resizeStartWidth.value - deltaX
    : resizeStartWidth.value + deltaX;

  const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
  sidebarWidth.value = clampedWidth;
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

const startResize = event => {
  // Only allow resize on desktop (md and above)
  if (!isDesktop.value || shouldShowCollapsedSidebar.value) return;

  isResizing.value = true;
  resizeStartX.value = event.clientX;
  resizeStartWidth.value = sidebarWidth.value;

  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  event.preventDefault();
};

const sidebarStyle = computed(() => {
  // Only apply custom width on desktop (md and above)
  if (!isDesktop.value) return {};
  const width = shouldShowCollapsedSidebar.value
    ? COLLAPSED_WIDTH
    : sidebarWidth.value;
  return {
    width: `${width}px`,
    flexBasis: `${width}px`,
    maxWidth: `${width}px`,
  };
});

const toggleSidebarCollapsed = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};
const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    ...(isInternalCommunicationEnabled.value
      ? [
          {
            name: 'Internal Communication',
            label: t('INTERNAL_COMMUNICATION.NAVIGATION.LABEL'),
            icon: 'i-lucide-users-round',
            to: accountScopedRoute('internal_communication'),
            activeOn: [
              'internal_communication',
              'internal_communication_room',
              'internal_communication_section',
            ],
            getterKeys: {
              count: 'internalCommunication/getUnreadCount',
            },
          },
        ]
      : []),
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'CRM',
      label: t('CRM.board.title'),
      icon: 'i-lucide-columns-3',
      to: accountScopedRoute('crm_board'),
      activeOn: ['crm_board'],
    },
    ...(isMediaLibraryEnabled.value
      ? [
          {
            name: 'Media Library',
            label: t('MEDIA_LIBRARY.TITLE'),
            icon: 'i-lucide-library',
            to: accountScopedRoute('media_library'),
            activeOn: ['media_library'],
          },
        ]
      : []),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      ...(currentAccount.value?.settings?.contacts_portfolio_enabled === true
        ? {
            to: accountScopedRoute('contacts_dashboard_index'),
            activeOn: ['contacts_dashboard_index', 'contacts_edit'],
          }
        : {}),
      children:
        currentAccount.value?.settings?.contacts_portfolio_enabled === true
          ? undefined
          : [
              {
                name: 'All Contacts',
                label: t('SIDEBAR.ALL_CONTACTS'),
                to: accountScopedRoute(
                  'contacts_dashboard_index',
                  {},
                  { page: 1, search: undefined }
                ),
                activeOn: ['contacts_dashboard_index', 'contacts_edit'],
              },
              {
                name: 'Active',
                label: t('SIDEBAR.ACTIVE'),
                to: accountScopedRoute('contacts_dashboard_active'),
                activeOn: ['contacts_dashboard_active'],
              },
              {
                name: 'Segments',
                icon: 'i-lucide-group',
                label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
                children: contactCustomViews.value.map(view => ({
                  name: `${view.name}-${view.id}`,
                  label: view.name,
                  to: accountScopedRoute(
                    'contacts_dashboard_segments_index',
                    { segmentId: view.id },
                    { page: 1 }
                  ),
                  activeOn: [
                    'contacts_dashboard_segments_index',
                    'contacts_edit_segment',
                  ],
                })),
              },
              {
                name: 'Tagged With',
                icon: 'i-lucide-tag',
                label: t('SIDEBAR.TAGGED_WITH'),
                children: labels.value.map(label => ({
                  name: `${label.title}-${label.id}`,
                  label: label.title,
                  icon: h('span', {
                    class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
                    style: { backgroundColor: label.color },
                  }),
                  to: accountScopedRoute(
                    'contacts_dashboard_labels_index',
                    { label: label.title },
                    { page: 1, search: undefined }
                  ),
                  activeOn: [
                    'contacts_dashboard_labels_index',
                    'contacts_edit_label',
                  ],
                })),
              },
            ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Other Settings',
          label: t('ORGANIZED_SETTINGS.TITLE'),
          icon: 'i-lucide-settings',
          to: accountScopedRoute('other_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Agent Assignment',
          label: t('SIDEBAR.AGENT_ASSIGNMENT'),
          icon: 'i-lucide-user-cog',
          to: accountScopedRoute('assignment_policy_index'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ];
});

// #endregion
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-transform duration-200 ease-in-out md:static w-[200px] basis-[200px] md:ltr:translate-x-0 md:rtl:-translate-x-0"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'items-center': shouldShowCollapsedSidebar,
      },
    ]"
    :style="sidebarStyle"
  >
    <!-- Resize handle - only visible on desktop -->
    <div
      v-if="!shouldShowCollapsedSidebar"
      class="hidden md:block absolute top-0 bottom-0 ltr:right-0 rtl:left-0 w-1 cursor-col-resize hover:bg-n-strong/50 transition-colors z-50 group"
      @mousedown="startResize"
    >
      <div class="absolute inset-y-0 ltr:-right-1 rtl:-left-1 w-3" />
    </div>
    <section class="grid gap-2 mt-2 mb-4 w-full">
      <div
        class="flex gap-2 items-center min-w-0"
        :class="
          shouldShowCollapsedSidebar
            ? 'justify-center px-2'
            : 'ltr:pl-2 ltr:pr-4 rtl:pr-2 rtl:pl-4'
        "
      >
        <div
          v-if="!shouldShowCollapsedSidebar"
          class="grid flex-shrink-0 place-content-center"
        >
          <Logo variant="sidebar" class="h-5 w-auto ml-2" />
        </div>
        <div
          v-if="!shouldShowCollapsedSidebar"
          class="flex-shrink-0 w-px h-3 bg-n-strong"
        />
        <SidebarAccountSwitcher
          :is-collapsed="shouldShowCollapsedSidebar"
          :class="
            shouldShowCollapsedSidebar
              ? 'flex justify-center'
              : 'flex-grow -mx-1 min-w-0'
          "
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <div
        class="flex gap-2"
        :class="
          shouldShowCollapsedSidebar
            ? 'flex-col items-center px-2'
            : 'ltr:pl-2 ltr:pr-4 rtl:pr-2 rtl:pl-4'
        "
      >
        <RouterLink
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 h-7 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30 min-w-0"
          :class="shouldShowCollapsedSidebar ? 'w-8 justify-center' : 'w-full'"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
          <span
            v-if="!shouldShowCollapsedSidebar"
            class="flex-grow text-left truncate w-0"
          >
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            v-if="!shouldShowCollapsedSidebar"
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="!h-7 !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
        <Button
          :icon="
            shouldShowCollapsedSidebar
              ? 'i-lucide-panel-left-open'
              : 'i-lucide-panel-left-close'
          "
          color="slate"
          size="sm"
          class="!h-7 !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11"
          :class="{ 'order-first': shouldShowCollapsedSidebar }"
          :title="t('KEYBOARD_SHORTCUTS.TITLE.TOGGLE_SIDEBAR')"
          :aria-label="t('KEYBOARD_SHORTCUTS.TITLE.TOGGLE_SIDEBAR')"
          @click="toggleSidebarCollapsed"
        />
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 px-2 pb-5 no-scrollbar min-w-0 w-full"
    >
      <ul
        class="flex flex-col gap-1.5 m-0 list-none min-w-0"
        :class="{ 'items-center': shouldShowCollapsedSidebar }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          :is-collapsed="shouldShowCollapsedSidebar"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex flex-col flex-shrink-0 relative gap-1 justify-between items-center w-full"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[31px] h-8 bg-gradient-to-t from-n-solid-2 to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !shouldShowCollapsedSidebar
        "
      />
      <RouterLink
        v-if="canOpenEssential"
        :to="accountScopedRoute('essential_home')"
        class="mx-2 mb-1 flex h-8 w-[calc(100%-1rem)] items-center justify-center gap-2 rounded-lg bg-n-teal-9/10 px-3 text-sm font-medium text-n-teal-11 outline outline-1 outline-transparent hover:bg-n-teal-9/20"
      >
        <i class="i-lucide-inbox size-4 flex-shrink-0" />
        <span class="truncate">{{ t('SIDEBAR.OPEN_ESSENTIAL') }}</span>
      </RouterLink>
      <div
        class="p-1 flex-shrink-0 flex w-full z-10 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="
          shouldShowCollapsedSidebar ? 'justify-center' : 'justify-between'
        "
      >
        <SidebarProfileMenu
          :is-collapsed="shouldShowCollapsedSidebar"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
  </aside>
</template>
